import React, { createContext, useState, useContext, useCallback } from 'react';

interface APIContextType {
  isLoading: boolean;
  wrapAPICall: <T>(promise: Promise<T>) => Promise<T>;
  wrapXHRCall: <T>(
    xhrCreator: () => XMLHttpRequest,
    processResponse: (xhr: XMLHttpRequest) => T
  ) => Promise<T>;
}

const APIContext = createContext<APIContextType | null>(null);

export const useAPI = () => {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error('useAPI must be used within an APIProvider');
  }
  return context;
};

interface APIProviderProps {
  children: React.ReactNode;
}

export const APIProvider: React.FC<APIProviderProps> = ({ children }) => {
  const [pendingRequests, setPendingRequests] = useState(0);

  const wrapAPICall = useCallback(async <T,>(promise: Promise<T>): Promise<T> => {
    setPendingRequests((prev) => prev + 1);
    try {
      const result = await promise;
      return result;
    } finally {
      setPendingRequests((prev) => Math.max(0, prev - 1));
    }
  }, []);

  const wrapXHRCall = useCallback(
    async <T,>(
      xhrCreator: () => XMLHttpRequest,
      processResponse: (xhr: XMLHttpRequest) => T
    ): Promise<T> => {
      setPendingRequests((prev) => prev + 1);

      return new Promise((resolve, reject) => {
        const xhr = xhrCreator();

        const cleanup = () => {
          setPendingRequests((prev) => Math.max(0, prev - 1));
        };

        xhr.addEventListener('load', () => {
          cleanup();
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = processResponse(xhr);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          } else {
            reject(new Error(`Request failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          cleanup();
          reject(new Error('Network error occurred'));
        });

        xhr.addEventListener('abort', () => {
          cleanup();
          reject(new Error('Request cancelled'));
        });
      });
    },
    []
  );

  return (
    <APIContext.Provider
      value={{
        isLoading: pendingRequests > 0,
        wrapAPICall,
        wrapXHRCall,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};
